import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import {
  AQUAMARINE,
  AQUAMARINE_SOFT,
  WARNING,
  WARNING_SOFT,
} from '../../../theme';
import { isItemDone } from './launchState';
import type { AutoCheckContext, CheckpointItem } from './types';

type Props = {
  items: CheckpointItem[];
  confirmed: Set<string>;
  context: AutoCheckContext;
  /** The step was edited after confirming, so its confirmations were cleared. */
  wasReset: boolean;
  disabled: boolean;
  onToggle: (itemId: string, checked: boolean) => void;
};

export const Checkpoint: React.FC<Props> = ({
  items,
  confirmed,
  context,
  wasReset,
  disabled,
  onToggle,
}) => {
  const doneCount = items.filter((item) =>
    isItemDone(item, confirmed, context),
  ).length;
  const allDone = doneCount === items.length;

  return (
    <Box
      role="group"
      aria-labelledby="checkpoint-heading"
      sx={{
        border: `1.5px ${allDone ? 'solid' : 'dashed'} ${allDone ? AQUAMARINE : WARNING}`,
        borderRadius: 2.5,
        bgcolor: allDone ? AQUAMARINE_SOFT : WARNING_SOFT,
        px: 2.25,
        py: 2,
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography
          id="checkpoint-heading"
          variant="subtitle1"
          fontWeight={600}
        >
          Checkpoint
        </Typography>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ ml: 'auto', color: allDone ? 'secondary.main' : WARNING }}
        >
          {doneCount} of {items.length} confirmed
        </Typography>
      </Stack>
      {wasReset && (
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ color: WARNING, mb: 1 }}
        >
          You changed this step after confirming, so confirmations were cleared.
          Check them again.
        </Typography>
      )}
      <Stack spacing={0.5}>
        {items.map((item) => (
          <FormControlLabel
            key={item.id}
            sx={{ alignItems: 'flex-start' }}
            control={
              <Checkbox
                size="small"
                sx={{ pt: 0.25 }}
                checked={isItemDone(item, confirmed, context)}
                disabled={disabled || Boolean(item.isAutoDone)}
                onChange={(event) => onToggle(item.id, event.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">{item.label}</Typography>
                {item.help && (
                  <Typography variant="caption" color="text.disabled">
                    {item.help}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}
      </Stack>
    </Box>
  );
};
